import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Hook personalizado para manejar query params de la URL
 * @returns Objeto con funciones para obtener y actualizar params
 */
const useUrlParams = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const getParam = useCallback((key: string): string | null => {
        const params = new URLSearchParams(location.search);
        return params.get(key);
    }, [location.search]);

    const getMultipleParams = useCallback((key: string): string[] => {
        const params = new URLSearchParams(location.search);
        return params.getAll(key);
    }, [location.search]);

    const getAllParams = useCallback((): Record<string, string> => {
        const params = new URLSearchParams(location.search);
        const allParams: Record<string, string> = {};
        params.forEach((value, key) => {
            allParams[key] = value;
        });
        return allParams;
    }, [location.search]);

    const updateParams = useCallback(
        (updates: Record<string, string | null | string[]>, replace: boolean = false) => {
            const params = new URLSearchParams(location.search);
            Object.entries(updates).forEach(([key, value]) => {
                params.delete(key); // limpiamos antes para evitar duplicados
                if (Array.isArray(value)) {
                    value.forEach((item) => params.append(key, item));
                } else if (value !== null) {
                    params.set(key, value);
                }
            });

            navigate({ search: params.toString() }, { replace });
        },
        [location.search, navigate]
    );

    const removeParams = useCallback((
        keys: string[],
        replace: boolean = false
    ) => {
        const params = new URLSearchParams(location.search);
        keys.forEach(key => params.delete(key));

        navigate({ search: params.toString() }, { replace });
    }, [location.search, navigate]);

    const clearParams = useCallback((replace: boolean = false) => {
        navigate({ search: '' }, { replace });
    }, [navigate]);

    const setParam = useCallback((
        key: string,
        value: string | null,
        replace: boolean = false
    ) => {
        updateParams({ [key]: value }, replace);
    }, [updateParams]);

    return {
        getParam,
        getMultipleParams,
        getAllParams,
        updateParams,
        removeParams,
        clearParams,
        setParam,
    };
};

export default useUrlParams;